/**
 * REPORT_TYPES — one entry per att_report_types id, and the only place the portal
 * says anything about a report type.
 *
 * Before this, a report's facts were spread over seven lists that did not reference
 * one another (docs/DUPLICATION_AUDIT.md, B2): its route and sub-type, whether a
 * schedule may edit its range or time-of-day window, which filter groups its schedule
 * editor shows, whether it has a CSV, whether it has filters at all, and the id each
 * page passed to the Create Schedule drawer. Caregiver (15) and Shift (16) were missed
 * in three of them. Add a report by adding one entry here (see
 * new-report-registration-points for the backend half).
 *
 * Fields:
 *   id             — att_report_types.report_type_id
 *   name           — for code and logs (the UI label comes from the DB)
 *   route          — page "Run Report Local" opens
 *   family         — 'wellness' / 'environmental' for the multi-report pages
 *   subType        — pre-selects the report on such a page (wellness: string,
 *                    environmental: environment_type_id)
 *   filters        — filter-group preset (components/v2/reportFilterSections.js)
 *   responseTime   — the Response Time filter applies
 *   hasFilters     — the schedule editor offers Edit Filters (4.0 Maintenance has none)
 *   editableRange  — a schedule may change its reporting interval. Executive and
 *                    Maintenance always run over a fixed range.
 *   editableWindow — a schedule may set a time-of-day window (procs taking
 *                    startMinute/endMinute, docs/REPORT_TIME_WINDOW_PLAN.md). Shift
 *                    Breakdown is excluded on purpose: its Events / Hour uses the
 *                    campus shift map's hours, so a window would understate it up to 4x
 *                    (docs/SHIFT_REPORT_PLAN.md section 7).
 *   csvDetails     — offers the CSV (Details) attachment (Executive has no detail CSV)
 *   cloud          — overrides for 5.0 cloud communities (Maintenance: its own filter
 *                    groups, which a schedule may edit). Read through the helpers' isCloud.
 *   rangeLabel     — what the schedule's range is called when it has none to speak of.
 *                    EHR Tasks lists everything pending, but its schedules store interval
 *                    1 (Today) so report_generator's date gate passes; this is shown instead
 *                    of the stored interval name.
 */

import {
  ALERT_REPORT_SECTIONS,
  WELLNESS_REPORT_SECTIONS,
  CHECKIN_REPORT_SECTIONS,
  ROUNDS_REPORT_SECTIONS,
  ENVIRONMENTAL_REPORT_SECTIONS,
  MAINTENANCE_REPORT_SECTIONS,
  CLOUD_MAINTENANCE_REPORT_SECTIONS,
  CALL_LOGS_REPORT_SECTIONS,
  EHR_TASKS_REPORT_SECTIONS,
} from '../components/v2/reportFilterSections.js';

const ALERT = { filters: ALERT_REPORT_SECTIONS, responseTime: true, hasFilters: true, csvDetails: true };
const WELLNESS = { family: 'wellness', filters: WELLNESS_REPORT_SECTIONS, responseTime: false, hasFilters: true, csvDetails: true, editableRange: true, editableWindow: false };
const ENVIRONMENTAL = { family: 'environmental', route: '/reports/environmental', filters: ENVIRONMENTAL_REPORT_SECTIONS, responseTime: false, hasFilters: true, csvDetails: true, editableRange: true, editableWindow: false };

export const REPORT_TYPES = [
  { id: 1, name: 'Alert Details', route: '/reports/alerts/run', subType: null, ...ALERT, editableRange: true, editableWindow: true },
  { id: 2, name: 'Rounds', route: '/reports/rounds', subType: 'rounds', ...WELLNESS, filters: ROUNDS_REPORT_SECTIONS },
  { id: 3, name: 'Check-in', route: '/reports/checkin', subType: 'check-in', ...WELLNESS, filters: CHECKIN_REPORT_SECTIONS },
  { id: 4, name: 'Hourly Summary', route: '/reports/hourly', subType: null, ...ALERT, editableRange: true, editableWindow: true },
  { id: 5, name: 'Executive', route: '/reports/executive', subType: null, ...ALERT, editableRange: false, editableWindow: false, csvDetails: false },
  { id: 6, name: 'Temperature', subType: 1, ...ENVIRONMENTAL },
  { id: 7, name: 'Humidity', subType: 2, ...ENVIRONMENTAL },
  { id: 8, name: 'Custom Low Battery', subType: 3, ...ENVIRONMENTAL },
  { id: 9, name: 'Missed Check-in', route: '/reports/checkin', subType: 'missed-check-in', ...WELLNESS, filters: CHECKIN_REPORT_SECTIONS },
  { id: 10, name: 'Maintenance', route: '/reports/maintenance', subType: null, filters: MAINTENANCE_REPORT_SECTIONS, responseTime: false, hasFilters: false, csvDetails: true, editableRange: false, editableWindow: false,
    cloud: { filters: CLOUD_MAINTENANCE_REPORT_SECTIONS, hasFilters: true } },
  { id: 11, name: 'Check-in History', route: '/reports/checkin', subType: 'check-in-history', ...WELLNESS },
  { id: 12, name: 'Full Battery', subType: 4, ...ENVIRONMENTAL },
  { id: 13, name: 'Rounds History', route: '/reports/rounds', subType: 'rounds-history', ...WELLNESS },
  { id: 14, name: 'Call Logs', route: '/reports/call-logs', subType: null, filters: CALL_LOGS_REPORT_SECTIONS, responseTime: false, hasFilters: true, csvDetails: true, editableRange: true, editableWindow: false },
  { id: 15, name: 'Caregiver', route: '/reports/caregiver', subType: null, ...ALERT, editableRange: true, editableWindow: true },
  { id: 16, name: 'Shift Breakdown', route: '/reports/shifts', subType: null, ...ALERT, editableRange: true, editableWindow: false },
  /* docs/EHR_TASKS_REPORT_PLAN.md: every pending task, no range or window. */
  { id: 17, name: 'EHR Tasks', route: '/reports/ehr-tasks', subType: null, filters: EHR_TASKS_REPORT_SECTIONS, responseTime: false, hasFilters: true, csvDetails: true, editableRange: false, editableWindow: false,
    rangeLabel: 'All pending tasks' },
];

/** Named ids for the pages that pass a fixed type to the Create Schedule drawer. */
export const REPORT_TYPE_ID = {
  ALERT_DETAILS: 1,
  HOURLY_SUMMARY: 4,
  EXECUTIVE: 5,
  MAINTENANCE: 10,
  CALL_LOGS: 14,
  CAREGIVER: 15,
  SHIFT_BREAKDOWN: 16,
  EHR_TASKS: 17,
};

const BY_ID = new Map(REPORT_TYPES.map((t) => [t.id, t]));

/** The registry entry, or null. Accepts a number or numeric string (API rows vary). */
export function getReportType(reportTypeId) {
  return BY_ID.get(parseInt(reportTypeId, 10)) || null;
}

/** The entry with its cloud overrides applied when isCloud (the campus's is_cloud). */
function reportTypeFor(reportTypeId, isCloud) {
  const t = getReportType(reportTypeId);
  return t && isCloud && t.cloud ? { ...t, ...t.cloud } : t;
}

/** The filter groups a report type's page and schedule show on this kind of campus. */
export function reportFilterSectionsFor(reportTypeId, { isCloud = false } = {}) {
  return reportTypeFor(reportTypeId, isCloud)?.filters;
}

/**
 * The report type a multi-report page is showing, from its sub-type selector.
 * Compared as strings: the environmental select can hand back "1" or 1.
 *
 * @param {'wellness'|'environmental'} family
 */
export function reportTypeIdForSubType(family, subType, fallbackId) {
  const found = REPORT_TYPES.find((t) => t.family === family && String(t.subType) === String(subType));
  return found ? found.id : fallbackId;
}

/**
 * The filter editor for a scheduled report type: which groups, and whether Response
 * Time is offered — the same as that report's live page. Unknown types get the alert
 * groups without Response Time (the previous fallback).
 *
 * @returns {{ sections: Array, responseTime: boolean }}
 */
export function scheduleFilterConfig(reportTypeId, { isCloud = false } = {}) {
  const t = reportTypeFor(reportTypeId, isCloud);
  return t ? { sections: t.filters, responseTime: t.responseTime } : { sections: ALERT_REPORT_SECTIONS, responseTime: false };
}

/** A schedule of this type may change its reporting interval. */
export function scheduleSupportsTimeRange(reportTypeId) {
  return Boolean(getReportType(reportTypeId)?.editableRange);
}

/** A schedule of this type may set a time-of-day window. */
export function scheduleSupportsTimeWindow(reportTypeId) {
  return Boolean(getReportType(reportTypeId)?.editableWindow);
}

/** Where "Run Report Local" goes: { path, subType }, or nulls for an unknown type. */
export function resolveScheduleRoute(reportTypeId) {
  const t = getReportType(reportTypeId);
  return t && t.route ? { path: t.route, subType: t.subType ?? null } : { path: null, subType: null };
}

/** The range a schedule shows: the type's rangeLabel, else the stored interval name. */
export function scheduleRangeLabel(reportTypeId, intervalName) {
  return getReportType(reportTypeId)?.rangeLabel || intervalName;
}

/** The schedule drawers offer the CSV (Details) attachment. Unknown types: yes. */
export function offersCsvDetails(reportTypeId) {
  return getReportType(reportTypeId)?.csvDetails !== false;
}

/** The schedule editor offers Edit Filters. Unknown types: yes. */
export function hasScheduleFilters(reportTypeId, { isCloud = false } = {}) {
  return reportTypeFor(reportTypeId, isCloud)?.hasFilters !== false;
}
